import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'cta' | 'primary' | 'outline' | 'ghost' | 'danger' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconPosition?: 'start' | 'end';
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  cta: 'bg-secondary-500 text-white hover:bg-secondary-600 focus:ring-secondary-300',
  primary: 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-300',
  outline:
    'border border-secondary-500 text-secondary-600 hover:bg-secondary-50 focus:ring-secondary-200',
  ghost: 'text-foreground-700 hover:bg-background-100 focus:ring-background-300',
  danger:
    'border border-danger-500 text-danger-600 hover:bg-danger-50 focus:ring-danger-200',
  soft: 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200 focus:ring-secondary-200',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-[13px] gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-12 px-6 text-[15px] gap-2',
};

export default function Button({
  variant = 'cta',
  size = 'md',
  icon,
  iconPosition = 'start',
  loading = false,
  fullWidth = false,
  className = '',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center rounded-btn font-medium whitespace-nowrap transition-colors duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const classes = [
    base,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classes} disabled={disabled || loading} {...rest}>
      {loading && (
        <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true"></i>
      )}
      {!loading && icon && iconPosition === 'start' && (
        <i className={`${icon} text-base leading-none`} aria-hidden="true"></i>
      )}
      {children}
      {!loading && icon && iconPosition === 'end' && (
        <i className={`${icon} text-base leading-none`} aria-hidden="true"></i>
      )}
    </button>
  );
}